import type { i18n as I18nInstance } from 'i18next';

import type { PartnerResourceLoader, ResourceLoader } from './types';

/**
 * Overlays a partner's label overrides on top of the base translations for the
 * given language.
 *
 * Idempotent and switch-safe: every namespace is reset to its base bundle
 * *before* the partner overlay is applied, so switching to a different partner —
 * or clearing the partner entirely (`partnerKey` undefined) — never leaves a
 * previous partner's overrides behind. Overlays customise keys that already
 * exist in the base bundle, so re-adding the base restores any label the
 * previous partner had changed.
 */
export async function mergePartnerOverrides(
  instance: I18nInstance,
  lng: string,
  partnerKey: string | undefined,
  loader: PartnerResourceLoader,
  baseLoader: ResourceLoader,
): Promise<void> {
  const namespacesOption = instance.options.ns;
  const namespaces: readonly string[] = Array.isArray(namespacesOption)
    ? namespacesOption
    : typeof namespacesOption === 'string'
      ? [namespacesOption]
      : [];

  await Promise.all(
    namespaces.map(async (ns) => {
      // Reset to base defaults first so a previously-applied partner's overrides
      // don't linger when the partner changes or is cleared.
      const base = await baseLoader(lng, ns).catch(() => undefined);
      if (base) {
        instance.addResourceBundle(lng, ns, base, true, true);
      }

      if (!partnerKey) return;

      const overlay = await loader(lng, partnerKey, ns).catch(() => undefined);
      if (overlay) {
        instance.addResourceBundle(lng, ns, overlay, true, true);
      }
    }),
  );
}
